import { Calendar, MapPin, Shield, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import CountdownTimer from './CountdownTimer';
import { EARLY_BIRD_DEADLINE, EVENT_DATE_LABEL, EVENT_DAY_LABEL } from '../data/eventConfig';
import { trainings } from '../data/trainings';

const EventBanner = () => {
  const liveTrainings = trainings.filter(t => t.status === 'upcoming' || t.status === 'active');
  const threeDayCount = liveTrainings.filter(t => t.duration === '3 Days').length;
  const twoDayCount = liveTrainings.filter(t => t.duration === '2 Days').length;
  const oneDayCount = liveTrainings.filter(t => t.duration === '1 Day').length;

  return (
    <section className="relative py-24 md:py-24 pb-8 md:pb-8 overflow-hidden bg-gradient-to-b from-[var(--surface)] to-transparent border-b border-[var(--border)]">
      {/* Background Effects */}
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[var(--primary)] to-transparent" />
      </div>

      {/* Decorative corner accents */}
      <div className="absolute top-4 left-4 w-2 h-2 bg-[var(--primary)] opacity-60" />
      <div className="absolute top-4 right-4 w-2 h-2 bg-[var(--accent-cyan)] opacity-60" />
      <div className="absolute bottom-4 left-4 w-2 h-2 bg-[var(--accent-cyan)] opacity-60" />
      <div className="absolute bottom-4 right-4 w-2 h-2 bg-[var(--primary)] opacity-60" />

      <div className="container-main relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          {/* Event Title */}
          <div className="animate-fade-in-up space-y-4">
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter text-[var(--text-main)] leading-tight">
              <span className="text-[var(--primary)]">BYT3</span>
              <span>CON</span><span className="text-[var(--primary)]"> Trainings</span>
              <br />
              <span className="text-3xl sm:text-4xl md:text-6xl font-bold text-[var(--accent-cyan)]">September 2026</span>
            </h1>
            <p className="text-lg md:text-xl text-[var(--text-muted)] font-mono">
              Build AI agents that hunt vulnerabilities. Break AI systems before attackers do. Use AI as a daily force multiplier in recon, threat modeling &amp; DevSecOps — hands-on, from engineers doing this in production.
            </p>
          </div>

          {/* Audience Badge */}
          <div className="animate-fade-in-up delay-100 inline-flex items-center gap-2 border-2 border-[var(--primary)] bg-[var(--surface-highlight)]/40 px-4 sm:px-6 py-2 shadow-[0_0_20px_var(--glow-primary)]">
            <Shield className="w-4 h-4 text-[var(--primary)] flex-shrink-0" />
            <span className="font-mono font-bold text-xs sm:text-sm uppercase tracking-widest text-[var(--primary)]">
              Curated for Security Engineers Using &amp; Securing AI
            </span>
          </div>

          {/* Event Details */}
          <div className="animate-fade-in-up delay-200 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-center justify-center max-w-4xl mx-auto">
            {/* Date */}
            <div className="flex items-center justify-center gap-3 bg-[var(--surface-highlight)]/30 px-6 py-4 border border-[var(--primary)]/30 rounded-none hover:border-[var(--primary)]/60 transition-colors">
              <Calendar className="w-5 h-5 text-[var(--primary)]" />
              <div className="text-left">
                <p className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-wider">Date</p>
                <p className="text-[var(--text-main)] font-bold">{EVENT_DATE_LABEL}</p>
                <p className="text-[var(--text-muted)] text-xs font-mono">{EVENT_DAY_LABEL}</p>
              </div>
            </div>

            {/* Format */}
            <div className="flex items-center justify-center gap-3 bg-[var(--surface-highlight)]/30 px-6 py-4 border border-[var(--accent-cyan)]/30 rounded-none hover:border-[var(--accent-cyan)]/60 transition-colors">
              <MapPin className="w-5 h-5 text-[var(--accent-cyan)]" />
              <div className="text-left">
                <p className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-wider">Format</p>
                <p className="text-[var(--text-main)] font-bold">Online</p>
                <p className="text-[var(--text-muted)] text-xs font-mono">Live Instructor-Led</p>
              </div>
            </div>

            {/* Audience */}
            <div className="flex items-center justify-center gap-3 bg-[var(--surface-highlight)]/30 px-6 py-4 border border-[var(--primary)]/30 rounded-none hover:border-[var(--primary)]/60 transition-colors">
              <Shield className="w-5 h-5 text-[var(--primary)]" />
              <div className="text-left">
                <p className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-wider">Audience</p>
                <p className="text-[var(--text-main)] font-bold">Security Engineers</p>
              </div>
            </div>
          </div>

          {/* Countdown */}
          <div className="animate-fade-in-up delay-300">
            <CountdownTimer targetDate={EARLY_BIRD_DEADLINE} label="Early Bird Pricing Ends In" variant="banner" />
          </div>

          {/* CTA Buttons */}
          <div className="animate-fade-in-up delay-400 flex gap-4 justify-center flex-col sm:flex-row px-4 sm:px-0">
            <Link
              to="/services/quarterly-trainings/register"
              className="group relative inline-flex items-center justify-center gap-2 bg-[var(--primary)] text-black font-mono font-bold px-6 sm:px-10 md:px-12 py-4 uppercase tracking-wider sm:tracking-widest hover:bg-[var(--primary-dim)] transition-all duration-200 shadow-[0_0_20px_var(--glow-primary)] hover:shadow-[0_0_35px_var(--glow-primary)]"
            >
              Reserve Your Seat
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services/quarterly-trainings"
              className="group relative inline-flex items-center justify-center gap-2 border border-[var(--primary)] text-[var(--primary)] font-mono font-bold px-6 sm:px-10 md:px-12 py-4 uppercase tracking-wider sm:tracking-widest hover:bg-[var(--surface-highlight)] transition-all duration-200"
            >
              View Full Lineup
            </Link>
          </div>

          {/* Decorative accent line */}
          <div className="animate-fade-in-up delay-500 flex items-center justify-center gap-2 text-[var(--text-muted)] font-mono text-sm text-center">
            <span className="w-2 h-2 bg-[var(--primary)] rounded-full animate-pulse flex-shrink-0" />
            <span>{threeDayCount}× 3-Day + {twoDayCount}× 2-Day Intensives + {oneDayCount}× 1-Day Masterclasses · Attack, defend &amp; use AI daily</span>
            <span className="w-2 h-2 bg-[var(--accent-cyan)] rounded-full animate-pulse flex-shrink-0" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventBanner;
